import { useState, useEffect } from "react";

// Convert a YouTube/Vimeo/Matterport URL to an embeddable form. (Moved from PredictionPage so
// the gallery can be shared by the analysis page and the standalone listing detail page.)
export const toEmbedUrl = (url) => {
  if (!url) return url;
  try {
    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vimeo = url.match(/vimeo\.com\/(\d+)/);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
    return url;
  } catch {
    return url;
  }
};

export const getVideoThumbnail = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`;
  return null;
};

/**
 * Scrollable media strip (Floor Plan → Videos → Tours → Images) used on the analysis page and
 * the standalone listing detail page.
 *
 * Props:
 *   property      - listing/property object with image_urls / floor_plan_url / video_urls / tour_urls
 *   onImageClick  - optional (url, label, index, items) => void. When provided (analysis page),
 *                   the host owns the lightbox. When omitted (detail page), a built-in lightbox is used.
 */
export default function MediaGallery({ property, onImageClick }) {
  const [lightbox, setLightbox] = useState(null); // { items, index } for the built-in viewer

  const hasMedia = property?.image_urls?.length > 0 || property?.floor_plan_url ||
    property?.video_urls?.length > 0 || property?.tour_urls?.length > 0;

  // Keyboard nav for the built-in lightbox
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowLeft") setLightbox((lb) => lb && lb.index > 0 ? { ...lb, index: lb.index - 1 } : lb);
      else if (e.key === "ArrowRight") setLightbox((lb) => lb && lb.index < lb.items.length - 1 ? { ...lb, index: lb.index + 1 } : lb);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  if (!hasMedia) return null;

  // Build combined gallery array. Order: floor plan → videos → tours → images.
  const galleryItems = [];
  if (property.floor_plan_url) galleryItems.push({ url: property.floor_plan_url, label: "Floor Plan", type: "floor_plan" });
  (property.video_urls || []).forEach((url, idx) => galleryItems.push({
    url: toEmbedUrl(url), originalUrl: url, label: `Video${property.video_urls.length > 1 ? " " + (idx + 1) : ""}`, type: "video",
  }));
  (property.tour_urls || []).forEach((url, idx) => galleryItems.push({
    url: toEmbedUrl(url), originalUrl: url, label: `3D Tour${property.tour_urls.length > 1 ? " " + (idx + 1) : ""}`, type: "tour",
  }));
  (property.image_urls || []).forEach((url, idx) => galleryItems.push({ url, label: `Image ${idx + 1}`, type: "image" }));

  const handleClick = (item, index) => {
    if (onImageClick) onImageClick(item.url, item.label, index, galleryItems);
    else if (item.type === "video" || item.type === "tour") window.open(item.originalUrl || item.url, "_blank");
    else setLightbox({ items: galleryItems.filter((g) => g.type === "image" || g.type === "floor_plan"), index: 0, target: item });
  };

  const renderTile = (item, index) => {
    const isVideo = item.type === "video";
    const isTour = item.type === "tour";
    const isFloorPlan = item.type === "floor_plan";
    const ytThumb = isVideo ? getVideoThumbnail(item.originalUrl) : null;
    const commonStyle = { minWidth: "250px", height: "200px", position: "relative", borderRadius: "8px", overflow: "hidden", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" };

    if (isFloorPlan) {
      return (
        <div key={`fp-${index}`} style={{ ...commonStyle, border: "2px solid #4CAF50" }}>
          <div style={{ width: "100%", height: "100%", backgroundImage: `url(${item.url})`, backgroundSize: "cover", backgroundPosition: "center" }} onClick={() => handleClick(item, index)} />
          <div style={{ position: "absolute", top: "8px", left: "8px", backgroundColor: "#4CAF50", color: "white", padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: "600" }}>Floor Plan</div>
        </div>
      );
    }
    if (isVideo || isTour) {
      const bg = ytThumb ? `url(${ytThumb}) center/cover` : isTour ? "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)" : "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)";
      const badgeColor = isTour ? "#6a11cb" : "#e53935";
      return (
        <div key={`${item.type}-${index}`} style={{ ...commonStyle, background: bg, border: `2px solid ${badgeColor}` }} onClick={() => handleClick(item, index)}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: ytThumb ? "rgba(0,0,0,0.3)" : "transparent" }}>
            <div style={{ width: "60px", height: "60px", borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.95)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px", color: "#000", boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>▶</div>
          </div>
          <div style={{ position: "absolute", top: "8px", left: "8px", backgroundColor: badgeColor, color: "white", padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: "600" }}>{item.label}</div>
        </div>
      );
    }
    return (
      <div key={`img-${index}`} style={{ ...commonStyle, backgroundImage: `url(${item.url})`, backgroundSize: "cover", backgroundPosition: "center" }} onClick={() => handleClick(item, index)} />
    );
  };

  // Resolve built-in lightbox to the actual clicked target's position within the image subset
  const lbItems = lightbox?.items || [];
  const lbIndex = lightbox ? Math.max(0, lbItems.findIndex((g) => g.url === lightbox.target?.url)) : 0;
  const lbActive = lightbox ? (lbItems[lightbox.index ?? lbIndex] || lbItems[lbIndex]) : null;

  return (
    <div style={{ marginBottom: "24px", position: "relative" }}>
      <div style={{ display: "flex", gap: "12px", overflowX: "auto", overflowY: "hidden", paddingBottom: "12px", scrollBehavior: "smooth", WebkitOverflowScrolling: "touch", scrollbarWidth: "thin", scrollbarColor: "#888 #f1f1f1" }}>
        {galleryItems.map((item, index) => renderTile(item, index))}
      </div>
      <style>{`
        div::-webkit-scrollbar { height: 8px; }
        div::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
        div::-webkit-scrollbar-thumb { background: #888; border-radius: 4px; }
        div::-webkit-scrollbar-thumb:hover { background: #555; }
      `}</style>

      {/* Built-in lightbox (only used when the host didn't supply onImageClick) */}
      {lbActive && (
        <div onClick={() => setLightbox(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 11000 }}>
          <span onClick={() => setLightbox(null)} style={{ position: "absolute", top: 16, right: 24, color: "#fff", fontSize: 32, cursor: "pointer" }}>&times;</span>
          {lbItems.length > 1 && (
            <span onClick={(e) => { e.stopPropagation(); setLightbox((lb) => ({ ...lb, index: Math.max(0, (lb.index ?? lbIndex) - 1) })); }}
              style={{ position: "absolute", left: 24, color: "#fff", fontSize: 40, cursor: "pointer", userSelect: "none" }}>‹</span>
          )}
          <img src={lbActive.url} alt={lbActive.label} onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8 }} />
          {lbItems.length > 1 && (
            <span onClick={(e) => { e.stopPropagation(); setLightbox((lb) => ({ ...lb, index: Math.min(lbItems.length - 1, (lb.index ?? lbIndex) + 1) })); }}
              style={{ position: "absolute", right: 24, color: "#fff", fontSize: 40, cursor: "pointer", userSelect: "none" }}>›</span>
          )}
        </div>
      )}
    </div>
  );
}
